import { checkRoutePermissions } from '@/ability/helpers';
import { InfoBoxState } from '@/models/InfoBox';
import router from '@/router/index';
import useInfoBoxState from '@/state/infoBox';
import usePageState from '@/state/pageState';
import useTokenState from '@/state/tokenState';
import store from '@/store';
import asyncWait from '@/utils/asyncWait';
import { isString } from 'lodash';
import useAgbState from '@/state/agbState';

import { INTERVAL_RESET, INTERVAL_STOP, intervalRefresherEventBus } from '@/mixins/useIntervalRefresher';
import { getDefaultRobots, isPrerenderBuild } from '@/utils/publicSite';
import { resetPrerenderReady, schedulePrerenderReady } from '@/utils/prerenderReady';
import { clearPublicSeo, toAbsolutePublicUrl, updatePublicSeo } from '@/utils/publicSeo';

const setNavigationGuards = () => {
  const {
    hasCustomSidebar, metaBoxState
  } = useInfoBoxState();

  const { title, pageNotFound } = usePageState();

  router.beforeEach(async (to, from, next) => {
    resetPrerenderReady(to.fullPath);

    // reset page load specific caches
    await store.dispatch('voting/resetVotingReporting');

    const requiresAuth = to.matched.some((record) => record.meta.auth);
    const canBeAccessed = to.meta?.permissions ? checkRoutePermissions(to.meta.permissions) : true;

    const { isLoggedIn } = useTokenState();

    if (requiresAuth && from.name === 'login' && to.name !== 'agb') {
      const shouldShowAgbPopupResult = await useAgbState().shouldShowAgbPopup();

      if (shouldShowAgbPopupResult) {
        next({
          name: 'agb',
          query: { redirectTo: to.fullPath },
        });
        return;
      }
    }

    const tokenParam = to.query.token;
    if (tokenParam) {
      await store.dispatch('loginWithToken', { token: tokenParam });

      if (to.name) {
        // Remove token query param from url
        router.replace({
          name: to.name,
          params: to.params,
          query: { ...to.query, token: undefined },
          hash: to.hash,
        });
      }
    }

    // set meta info
    const descEl = document.querySelector('head meta[name="description"]');
    const defaultDescription = process.env.VUE_APP_DESCRIPTION ?? '';

    const robotsEl = document.querySelector('head meta[name="robots"]');
    const defaultRobots = getDefaultRobots();

    title.value = to?.meta?.title ?? 'ByteCookie';

    if (descEl) {
      if (to?.meta?.description) {
        descEl.setAttribute('content', to.meta.description);
      } else {
        descEl.setAttribute('content', defaultDescription);
      }
    }

    if (robotsEl) {
      if (to?.meta?.robots) {
        robotsEl.setAttribute('content', to.meta.robots);
      } else {
        robotsEl.setAttribute('content', defaultRobots);
      }
    }

    /* default route => index */
    if (!to?.name) {
      pageNotFound.value = true;
      next({ name: 'index' });
      return;
    }

    if (isLoggedIn.value && !canBeAccessed) {
      const {
        registrationStep,
        registrationStepPlainName
      } = store.getters;

      if (registrationStep) {
        next({ name: registrationStep });
      } else if (registrationStepPlainName) {
        next({ name: registrationStepPlainName });
      } else {
        next({ name: 'register-status' });
      }
    }
    if (requiresAuth && !isLoggedIn.value && !['index'].includes(to.name as string)) {
      store.commit('removeRegistrationData');
      store.commit('setRedirectUrl', to);
      next({ name: 'login' });
    } else if (isLoggedIn.value && ['login', 'index'].includes(isString(to.name) ? to.name : 'index')) {
      next({ name: 'home', hash: to.hash, query: to.query });
    } else {
      next();
    }

    hasCustomSidebar.value = !!to?.meta?.sidebarComponent;
    metaBoxState.value = to?.meta?.infoBox ?? null as InfoBoxState | null;
  });

  router.afterEach(async (to, from) => {
    const isPublicRoute = to.matched.some((record) => record.meta?.layout === 'landingpage');
    const isComponentManagedPublicSeo = to.matched.some((record) => record.meta?.publicSeoHandledByComponent);
    const isComponentManagedPrerenderReady = to.matched.some((record) => record.meta?.publicPrerenderReadyHandledByComponent);
    const defaultRobots = getDefaultRobots();

    if (isPublicRoute && !isComponentManagedPublicSeo) {
      updatePublicSeo({
        title: to?.meta?.title ?? 'ByteCookie',
        description: to?.meta?.description ?? '',
        robots: to?.meta?.robots ?? defaultRobots,
        canonical: to?.meta?.canonical ?? toAbsolutePublicUrl(to.path),
        image: to?.meta?.image ?? toAbsolutePublicUrl('/BC_preview.png'),
        ogType: to?.meta?.ogType ?? 'website',
        twitterCard: to?.meta?.twitterCard ?? 'summary_large_image',
        structuredData: to?.meta?.structuredData
      });

      if (!isComponentManagedPrerenderReady) {
        await schedulePrerenderReady(to.fullPath);
      }
    } else if (!isPublicRoute) {
      clearPublicSeo();

      if (isPrerenderBuild()) {
        await schedulePrerenderReady(to.fullPath);
      }
    }

    // update notification status faster for some routes:
    const updatingRoutes = [
      'subscriptions',
      'manageSubscriptions',
      'projectForumThread',
      'thread',
      'recommendations'
    ];

    if (updatingRoutes.includes(to?.name ?? '')) {
      await asyncWait(200);
      intervalRefresherEventBus.emit(INTERVAL_RESET, `route change by updating routes (${updatingRoutes.join(',')})`);
    }

    // leaving logged in state:
    if (from.meta?.auth && !to.meta?.auth) {
      intervalRefresherEventBus.emit(INTERVAL_STOP);
    }

    // update route when coming from logged in state
    if (!from.meta?.auth && to.meta?.auth) {
      intervalRefresherEventBus.emit(INTERVAL_RESET, 'route change: coming from logged-out to logged-in state');
    }
  });
};

export default setNavigationGuards;
